import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Card, Input, List, Select, Space, Typography } from 'antd';
import { vendorApi, BuSettingsError } from '@/utils/vendorApi';
import { useAuthStore, businessUnitLabel } from '@/store/authStore';
import { canManageTags } from '@/store/vendorAccess';

const { Title, Paragraph } = Typography;

const BusinessUnitTagsPage: React.FC = () => {
  const { message } = App.useApp();
  const { authorization } = useAuthStore();
  const units = useMemo(() => {
    if (!authorization) {
      return [];
    }
    const ids = authorization.globalAdmin
      ? authorization.catalog.map((entry) => entry.id)
      : authorization.units.map((unit) => unit.id);
    return ids.filter((id) => canManageTags(authorization, id));
  }, [authorization]);

  const [bu, setBu] = useState<string | undefined>(units[0]);
  const [tags, setTags] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!bu && units[0]) {
      setBu(units[0]);
    }
  }, [bu, units]);

  const loadTags = useCallback(async (businessUnit: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      setTags(await vendorApi.listTags(businessUnit));
    } catch (error: unknown) {
      const text = error instanceof BuSettingsError ? error.message : 'Failed to load tags';
      setLoadError(text);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bu) {
      void loadTags(bu);
    }
  }, [bu, loadTags]);

  const addTag = async () => {
    const name = draft.trim();
    if (!bu || !name) {
      return;
    }
    setSaving(true);
    try {
      setTags(await vendorApi.addTag(bu, name));
      setDraft('');
      message.success(`Added ${name}`);
    } catch (error: unknown) {
      const text = error instanceof BuSettingsError ? error.message : 'Failed to add tag';
      message.error(text);
    } finally {
      setSaving(false);
    }
  };

  const removeTag = async (tag: string) => {
    if (!bu) {
      return;
    }
    setSaving(true);
    try {
      setTags(await vendorApi.deleteTag(bu, tag));
      message.success(`Removed ${tag}`);
    } catch (error: unknown) {
      const text = error instanceof BuSettingsError ? error.message : 'Failed to remove tag';
      message.error(text);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginBottom: 0 }}>Vendor Tags</Title>
      <Paragraph type="secondary" style={{ marginTop: 8, maxWidth: 720 }}>
        Tags label vendors inside a business unit. They do not change who can see or transfer files.
      </Paragraph>
      <Card style={{ maxWidth: 640 }}>
        <Select
          style={{ width: '100%', marginBottom: 16 }}
          value={bu}
          onChange={setBu}
          options={units.map((id) => ({ value: id, label: businessUnitLabel(id) }))}
          placeholder="Business unit"
        />
        {loadError && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} message={loadError} />
        )}
        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onPressEnter={() => void addTag()}
            placeholder="New tag"
            disabled={!bu || saving}
          />
          <Button type="primary" onClick={() => void addTag()} loading={saving} disabled={!bu}>
            Add tag
          </Button>
        </Space.Compact>
        <List
          loading={loading}
          dataSource={tags}
          locale={{ emptyText: 'No tags for this business unit' }}
          renderItem={(tag) => (
            <List.Item
              actions={[
                <Button key="remove" danger disabled={saving} onClick={() => void removeTag(tag)}>
                  Remove
                </Button>,
              ]}
            >
              {tag}
            </List.Item>
          )}
        />
      </Card>
    </div>
  );
};

export default BusinessUnitTagsPage;
